//@ts-nocheck
import styled, { css } from 'styled-components';
import {
  compose,
  color,
  ColorProps,
  space,
  SpaceProps,
  typography,
  TypographyProps,
  shadow,
  ShadowProps,
  display,
  DisplayProps,
  borders,
  BordersProps,
  layout,
  LayoutProps,
  ResponsiveValue,
  system
} from 'styled-system';
import * as CSS from 'csstype';
import DefaultUptimeTheme from '../theme/defaultTheme.tsx';
import { colorLuminance } from '../../utils/colors';

export interface IExposedButtonProps {
  disabled?: boolean;
  kind?: ButtonType;
  fullWidth?: boolean;
}

interface TextTransformProp {
  textTransform?: ResponsiveValue<CSS.TextTransformProperty>;
}

export type ButtonType =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'danger'
  | 'warning'
  | 'active'
  | 'impact1'
  | 'impact2'
  | 'accent'
  | 'text';

export type StyledButtonProps = ColorProps &
  SpaceProps &
  TypographyProps &
  ShadowProps &
  DisplayProps &
  BordersProps &
  LayoutProps &
  TextTransformProp &
  IExposedButtonProps & {
    /**
     * Render an icon on the left of button text.
     */
    withIconOnLeft?: React.ReactElement;

    /**
     * Render an icon on the right of button text.
     */
    withIconOnRight?: React.ReactElement;
  };

// Constants
const MIN_BUTTON_WIDTH = '0px';
const BUTTON_FULL_WIDTH = '100%';
const BUTTON_TRANSITION_TIMING = '0.1s ease-in';
const ICON_ALIGN_OFFSET = '-2px';

const textTransform = system({
  textTransform: {
    property: 'textTransform'
  }
});

const renderStyleProps = compose(
  color,
  space,
  typography,
  shadow,
  display,
  borders,
  layout,
  textTransform
);

const getDisabledStyles = (theme: any): string =>
  `&:disabled {
      border: none;
      outline: none;
      color: ${theme.colors.grayScale[5]};
      background: ${theme.colors.background.disabled};
    }`;

/*
  BaseButton holds our default values
  All other button should "extend" BaseButton
*/
const BaseButton = styled.button<StyledButtonProps>`
  ${({ theme, fullWidth, disabled, withIconOnLeft, withIconOnRight }) => `
    * {
      pointer-events: none;
    }
    border-radius: 4px;
    font-size: 14px;
    ${fullWidth &&
      css`
        width: ${BUTTON_FULL_WIDTH};
      `};
    min-width: ${MIN_BUTTON_WIDTH};

    padding: 9px ${withIconOnLeft || withIconOnRight ? 12 : 15}px;

    transition: color, border-color ${BUTTON_TRANSITION_TIMING};
    box-shadow: '0 1px 1px 0 rgba(183, 183, 183, 0.3)';
    border: 1px solid transparent;
    cursor: ${disabled ? 'not-allowed ' : 'pointer'};
    &:active {
      outline: none;
    }

    /* Icon styles. */
    svg {
      font-size: inherit;
      vertical-align: ${ICON_ALIGN_OFFSET};
      ${withIconOnLeft ? 'margin-right: 5px;' : ''}
      ${withIconOnRight ? 'margin-left: 5px;' : ''}
    }
    &:focus {
      outline-offset: 3px;
    }
  `}
`;

/**
 * Basic Buttons
 */
export const PrimaryButton = styled(BaseButton)`
  ${({ theme }) => `
    background: ${theme.colors.button.primary.enabled};
    color: ${theme.colors.button.text?.primary || theme.colors.grayScale[0]};
    &:hover {
      background: ${theme.colors.button.primary.hover};
    }
    &:focus {
      border: 1px solid ${theme.colors.button.primary.hover};
      outline-color: ${theme.colors.button.primary.hover};
      background: ${theme.colors.button.primary.hover};
    }
    &:active {
      background: ${theme.colors.button.primary.pressed};
    }
    &:visited {
      background: ${theme.colors.button.primary.enabled};
    }
    ${getDisabledStyles(theme)}
  `}
  ${renderStyleProps}
`;

export const SecondaryButton = styled(BaseButton)`
  ${({ theme }) => `
    background: transparent;
    border: 1px solid ${theme.colors.button.primary.enabled};
    color: ${theme.colors.button.text?.secondary ||
      theme.colors.button.primary.enabled};
    &:focus {
      border-color: ${theme.colors.button.primary.hover};
      outline-color: ${theme.colors.button.primary.hover};
      color: ${theme.colors.button.text?.secondary ||
        theme.colors.button.primary.hover};
    }
    &:hover {
      border-color: ${theme.colors.button.primary.hover};
      color: ${theme.colors.button.text?.secondary ||
        theme.colors.button.primary.hover};
    }
    &:active {
      border-color: ${theme.colors.button.primary.pressed};
      color: ${theme.colors.button.text?.secondary ||
        theme.colors.button.primary.pressed};
    }
    ${getDisabledStyles(theme)}
    &:disabled {
      border: 1px solid ${theme.colors.grayScale[5]};
      background: transparent;
    }
    &:visited {
      background: transparent;
    }
  `}
  ${renderStyleProps}
`;

export const AccentButton = styled(BaseButton)`
  ${({ theme }) => `
    background: ${theme.colors.button.accent.enabled};
    color: ${theme.colors.button.text?.accent || theme.colors.grayScale[0]};
    &:hover {
      background: ${theme.colors.button.accent.hover};
    }
    &:focus {
      background: ${theme.colors.button.accent.hover};
      border: 1px solid ${theme.colors.button.accent.hover};
      outline-color: ${theme.colors.button.accent.hover};
    }
    &:active {
      background: ${theme.colors.button.accent.pressed};
    }
    &:visited {
      background: ${theme.colors.button.accent.enabled};
    }
    ${getDisabledStyles(theme)}
  `}
  ${renderStyleProps}
`;

export const TextButton = styled(BaseButton)`
  ${({ theme }) => `
    box-shadow: none;
    color: ${theme.colors.button.text?.text ||
      theme.colors.button.primary.enabled};
    background-color: transparent;
    &:focus {
      outline-color: ${theme.colors.background.hover};
      background: ${theme.colors.background.hover};
      color: ${theme.colors.button.text?.text ||
        theme.colors.button.primary.hover};
    }
    &:hover {
      background: ${theme.colors.background.hover};
      color: ${theme.colors.button.text?.text ||
        theme.colors.button.primary.hover};
    }
    &:active {
      background: transparent;
      color: ${theme.colors.button.text?.text ||
        theme.colors.button.primary.pressed};
    }
    ${getDisabledStyles(theme)}
    &:disabled {
      background-color: transparent;
    }
  `}
  ${renderStyleProps}
`;

/**
 * "Stateful" Buttons
 */

export const SuccessButton = styled(BaseButton)`
  ${({ theme }) => `
    background-color: ${theme.colors.state.success};
    color: ${theme.colors.button.text?.success || theme.colors.grayScale[0]};
    &:focus {
      background: ${colorLuminance(theme.colors.state.success, 6, 'offset')};
      outline-color: ${colorLuminance(theme.colors.state.success, 6, 'offset')};
    }
    &:hover {
      background: ${colorLuminance(theme.colors.state.success, 6, 'offset')};
    }
    &:active {
      background: ${colorLuminance(theme.colors.state.success, -8, 'offset')};
    }
    ${getDisabledStyles(theme)}
  `}
  ${renderStyleProps}
`;

export const DangerButton = styled(BaseButton)`
  ${({ theme }) => `
    background-color: ${theme.colors.state.danger};
    color: ${theme.colors.button.text?.danger || theme.colors.grayScale[0]};
    &:focus {
      background: ${colorLuminance(theme.colors.state.danger, 11, 'offset')};
      outline-color: ${colorLuminance(theme.colors.state.danger, 11, 'offset')};
    }
    &:hover {
      background: ${colorLuminance(theme.colors.state.danger, 11, 'offset')};
    }
    &:active {
      background: ${colorLuminance(theme.colors.state.danger, -7, 'offset')};
    }
    ${getDisabledStyles(theme)}
  `}
  ${renderStyleProps}
`;

export const WarningButton = styled(BaseButton)`
  ${({ theme }) => `
    background: ${theme.colors.state.warning};
    color: ${theme.colors.button.text?.warning || theme.colors.grayScale[0]};
    &:focus {
      background: ${colorLuminance(theme.colors.state.warning, 10, 'offset')};
      outline-color: ${colorLuminance(
        theme.colors.state.warning,
        10,
        'offset'
      )};
    }
    &:hover {
      background: ${colorLuminance(theme.colors.state.warning, 10, 'offset')};
    }
    &:active {
      background: ${colorLuminance(theme.colors.state.warning, -10, 'offset')};
    }
    ${getDisabledStyles(theme)}
  `}
  ${renderStyleProps}
`;

export const ActiveButton = styled(BaseButton)`
  ${({ theme }) => `
    background-color: ${theme.colors.state.active};
    color: ${theme.colors.button.text?.active || theme.colors.grayScale[0]};
    &:focus {
      background: ${colorLuminance(theme.colors.state.active, 10, 'offset')};
      outline-color: ${colorLuminance(theme.colors.state.active, 10, 'offset')};
    }
    &:hover {
      background: ${colorLuminance(theme.colors.state.active, 10, 'offset')};
    }
    &:active {
      background: ${colorLuminance(theme.colors.state.active, -10, 'offset')};
    }
    ${getDisabledStyles(theme)}
  `}
  ${renderStyleProps}
`;

/**
 * Impact Buttons
 */

/**
 * Identifies correct color to use for gradient styles;
 * If no gradient is provided in the current theme, use the defualt Lumos theme
 * @param gradientIndex
 * @param colorIndex
 * @param gradients
 */
const getImpactColor = (
  gradientIndex: number,
  colorIndex: number,
  gradients?: string[][]
): string => {
  if (
    gradients &&
    gradients[gradientIndex] &&
    gradients[gradientIndex][colorIndex]
  )
    return gradients[gradientIndex][colorIndex];

  return DefaultUptimeTheme.colors.gradient[gradientIndex][colorIndex];
};

export const Impact1Button = styled(BaseButton)`
  ${({ theme }) => `
    border: 1px solid ${getImpactColor(0, 1, theme.colors.gradient)};
    background: linear-gradient(${getImpactColor(
      0,
      0,
      theme.colors.gradient
    )}, ${getImpactColor(0, 1, theme.colors.gradient)});
    background-origin: border-box;
    color: ${theme.colors.button.text?.impact1 || theme.colors.grayScale[0]};
    &:hover {
      border: 1px solid transparent;
    }
    &:active {
      outline: none;
      background: linear-gradient(${getImpactColor(
        0,
        1,
        theme.colors.gradient
      )}, ${getImpactColor(0, 0, theme.colors.gradient)});
      background-origin: border-box;
    }
    &:focus {
      outline-color: ${getImpactColor(0, 1, theme.colors.gradient)};
    }
    ${getDisabledStyles(theme)}
  `}
  ${renderStyleProps}
`;

export const Impact2Button = styled(BaseButton)`
  ${({ theme }) => `
    border: 1px solid ${getImpactColor(1, 1, theme.colors.gradient)};
    background: linear-gradient(${getImpactColor(
      1,
      0,
      theme.colors.gradient
    )}, ${getImpactColor(1, 1, theme.colors.gradient)});
    background-origin: border-box;
    color: ${theme.colors.button.text?.impact2 || theme.colors.primary[0]};
    &:hover {
      border: 1px solid transparent;
    }
    &:active {
      outline: none;
      background: linear-gradient(${getImpactColor(
        1,
        1,
        theme.colors.gradient
      )}, ${getImpactColor(1, 0, theme.colors.gradient)});
      background-origin: border-box;
    }
    &:focus {
      outline-color: ${getImpactColor(1, 1, theme.colors.gradient)};
    }
    ${getDisabledStyles(theme)}
  `}
  ${renderStyleProps}
`;
